import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, DollarSign, Shield, Clock } from 'lucide-react';

interface ProviderManualControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  providerName: string;
  action: 'grant_access' | 'update_dates' | 'apply_discount' | null;
  onGrantAccess: (providerId: string, reason?: string) => void;
  onUpdateDates: (providerId: string, startDate: string, endDate: string) => void;
  onApplyDiscount: (providerId: string, discount: number, type: 'percentage' | 'fixed' | 'none', notes?: string) => void;
  isLoading?: boolean;
}

const ProviderManualControlModal: React.FC<ProviderManualControlModalProps> = ({
  isOpen,
  onClose,
  providerId,
  providerName,
  action,
  onGrantAccess,
  onUpdateDates,
  onApplyDiscount,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [discount, setDiscount] = useState('0');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed' | 'none'>('percentage');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (action === 'grant_access') {
      onGrantAccess(providerId, reason);
    } else if (action === 'update_dates') {
      if (startDate && endDate) {
        onUpdateDates(providerId, startDate, endDate);
      }
    } else if (action === 'apply_discount') {
      onApplyDiscount(providerId, parseFloat(discount), discountType, notes);
    }
    onClose();
  };

  const renderContent = () => {
    switch (action) {
      case 'grant_access':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
              <Shield className="w-5 h-5 text-primary" />
              <p className="text-sm">
                This will activate the provider <strong>{providerName}</strong> without requiring payment.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="e.g., VIP user, testing, promotional access..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        );

      case 'update_dates':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
              <Calendar className="w-5 h-5 text-primary" />
              <p className="text-sm">
                Manually set subscription dates for <strong>{providerName}</strong>.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        );

      case 'apply_discount':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
              <DollarSign className="w-5 h-5 text-primary" />
              <p className="text-sm">
                Apply special pricing or discount for <strong>{providerName}</strong>.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="discountType">Discount Type</Label>
              <Select value={discountType} onValueChange={(value: any) => setDiscountType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {discountType !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="discount">
                  Discount {discountType === 'percentage' ? '(%)' : '($)'}
                </Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max={discountType === 'percentage' ? '100' : undefined}
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="notes">Special Plan Notes</Label>
              <Textarea
                id="notes"
                placeholder="Document special arrangements, agreements, or pricing details..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (action) {
      case 'grant_access':
        return 'Grant Provider Access';
      case 'update_dates':
        return 'Update Subscription Dates';
      case 'apply_discount':
        return 'Apply Discount';
      default:
        return 'Manual Control';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            Administrative manual control for provider management
          </DialogDescription>
        </DialogHeader>
        {renderContent()}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderManualControlModal;
